import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Code2, Crown, Shield, Truck, User, ChevronDown, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DevRoleSwitcher = () => {
  const { user, setUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Solo mostrar para usuarios DESARROLLO
  if (!user || user.role !== 'DESARROLLO') return null;

  const currentRoleId = user.simulated_role || null;

  const handleRoleChange = async (roleId) => {
    setSwitching(true);
    try {
      const response = await axios.post(`${API}/auth/simulate-role`, { 
        simulated_role: roleId 
      });
      
      setUser(response.data.user);
      
      const roleName = roleId || 'DESARROLLO';
      toast.success(`Vista cambiada a: ${roleName}`);
      
      setIsOpen(false);
      
      setTimeout(() => {
        window.location.reload();
      }, 500);
      
    } catch (error) {
      toast.error('Error al cambiar de vista');
    } finally {
      setSwitching(false);
    }
  };

  const getRoleIcon = (roleId) => {
    if (roleId === null) return <Code2 size={14} className="text-white" />;
    if (roleId === 'MASTER') return <Crown size={14} className="text-white" />;
    if (roleId === 'ADMIN') return <Shield size={14} className="text-white" />;
    if (roleId === 'TRANSPORTISTA') return <Truck size={14} className="text-white" />;
    if (roleId === 'CHOFER') return <User size={14} className="text-white" />;
    return <Code2 size={14} className="text-white" />;
  };

  const getRoleColor = (roleId) => {
    if (roleId === null) return 'bg-purple-500';
    if (roleId === 'MASTER') return 'bg-amber-500';
    if (roleId === 'ADMIN') return 'bg-blue-500';
    if (roleId === 'TRANSPORTISTA') return 'bg-emerald-500';
    if (roleId === 'CHOFER') return 'bg-slate-500';
    return 'bg-purple-500';
  };

  const getRoleLabel = (roleId) => {
    return roleId || 'DESARROLLO';
  };

  const roles = [
    { id: null, label: 'DESARROLLO', desc: 'Vista de desarrollo' },
    { id: 'MASTER', label: 'MASTER', desc: 'Acceso total' },
    { id: 'ADMIN', label: 'ADMIN', desc: 'Administrador' },
    { id: 'TRANSPORTISTA', label: 'TRANSPORTISTA', desc: 'Gestión de flota' },
    { id: 'CHOFER', label: 'CHOFER', desc: 'Vista de conductor' }
  ];

  return (
    <div className="fixed bottom-4 left-4 z-50" data-testid="dev-role-switcher">
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={switching}
          className="flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border-2 border-purple-300 bg-white hover:bg-purple-50 transition-all"
        >
          <div className={`w-6 h-6 rounded-full ${getRoleColor(currentRoleId)} flex items-center justify-center`}>
            {getRoleIcon(currentRoleId)}
          </div>
          <div className="text-left">
            <p className="text-[10px] text-purple-600 font-semibold leading-none">MODO DEV</p>
            <p className="text-xs font-bold text-slate-800">{getRoleLabel(currentRoleId)}</p>
          </div>
          <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute bottom-full left-0 mb-2 w-56 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-2 bg-purple-50 border-b border-purple-100">
              <div className="flex items-center gap-2">
                <Eye size={14} className="text-purple-600" />
                <span className="text-xs font-semibold text-purple-700">Cambiar vista de rol</span>
              </div>
            </div>
            <div className="p-1">
              {roles.map((role) => {
                const isActive = currentRoleId === role.id;
                return (
                  <button
                    key={role.id || 'dev'}
                    onClick={() => handleRoleChange(role.id)}
                    disabled={switching || isActive}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${isActive ? 'bg-purple-100' : 'hover:bg-slate-50'}`}
                  >
                    <div className={`w-8 h-8 rounded-full ${getRoleColor(role.id)} flex items-center justify-center flex-shrink-0`}>
                      {getRoleIcon(role.id)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${isActive ? 'text-purple-800' : 'text-slate-800'}`}>{role.label}</p>
                      <p className="text-[10px] text-slate-500 truncate">{role.desc}</p>
                    </div>
                    {isActive && <div className="w-2 h-2 rounded-full bg-purple-500"></div>}
                  </button>
                );
              })}
            </div>
            <div className="p-2 bg-slate-50 border-t border-slate-100">
              <p className="text-[9px] text-slate-400 text-center">Los cambios recargarán la página</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DevRoleSwitcher;
